import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText } from 'lucide-react'
import { listReports } from '../../api/reports'
import { getReport } from '../../api/reports'
import type { Report } from '../../api/schemas'
import { PageHeader } from '../../components/layout/PageHeader'
import { EmptyState, StatusBadge } from '../../components/common/Status'
import { TimeAgo } from '../../components/common/TimeAgo'
import { QueryBoundary } from '../../components/common/QueryBoundary'
import { Skeleton } from '../../components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog'
import { Button } from '../../components/ui/button'
import { formatMag, formatNum } from '../../lib/format'

const PAGE_SIZE = 20

/** 灾情报告中心：列表 + 详情弹窗 */
export function ReportsPage() {
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['reports', { page }],
    queryFn: () => listReports({ page, pageSize: PAGE_SIZE }),
    retry: 1,
  })

  const detailQuery = useQuery({
    queryKey: ['reports', 'detail', selectedId],
    queryFn: () => getReport(selectedId!),
    enabled: Boolean(selectedId),
    retry: 1,
  })

  const data = query.data
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  return (
    <>
      <PageHeader
        breadcrumb="应急处置"
        title="灾情报告"
        description="系统生成的地震灾情评估与影响范围报告"
      />

      <QueryBoundary
        query={query}
        skeleton={
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-11 rounded-md" />
            ))}
          </div>
        }
        isEmpty={(d) => d.items.length === 0}
        empty={{ title: '暂无灾情报告', description: '地震确认后系统将自动生成灾情评估报告', icon: <FileText size={28} /> }}
        className="panel-glass overflow-hidden rounded-xl"
      >
        {(d) => (
          <>
            <table className="w-full text-left">
              <thead className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">报告</th>
                  <th className="px-4 py-2.5 font-medium">关联事件</th>
                  <th className="px-4 py-2.5 font-medium text-right">震级</th>
                  <th className="px-4 py-2.5 font-medium text-right">影响半径</th>
                  <th className="px-4 py-2.5 font-medium text-right">触发/确认</th>
                  <th className="px-4 py-2.5 font-medium">状态</th>
                  <th className="px-4 py-2.5 font-medium">生成时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {d.items.map((r) => (
                  <tr
                    key={r.reportId}
                    onClick={() => setSelectedId(r.reportId)}
                    className="cursor-pointer text-xs transition hover:bg-foreground/[.03]"
                  >
                    <td className="px-4 py-2.5 font-medium text-foreground/90">{r.title || r.reportId}</td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{r.eventId || '—'}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-critical">{formatMag(r.magnitude)}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-muted-foreground">{r.affectedRadiusKm.toFixed(0)} km</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-muted-foreground">{r.triggeredDevices}/{r.confirmedDevices}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2.5"><TimeAgo ts={r.createdAt} className="text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              <span>共 {formatNum(d.total)} 份 · 第 {page}/{totalPages} 页</span>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  上一页
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  下一页
                </Button>
              </div>
            </div>
          </>
        )}
      </QueryBoundary>

      <ReportDetailDialog reportId={selectedId} query={detailQuery} onClose={() => setSelectedId(null)} />
    </>
  )
}

function ReportDetailDialog({ reportId, query, onClose }: {
  reportId: string | null
  query: { isPending: boolean; isError: boolean; error: Error | null; data?: Report }
  onClose: () => void
}) {
  const report = query.data
  const attachments = report?.attachments ? report.attachments.split(',').filter(Boolean) : []
  return (
    <Dialog open={Boolean(reportId)} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl">
        {reportId && (
          <>
            <DialogHeader>
              <DialogTitle>{report?.title ?? reportId}</DialogTitle>
              <DialogDescription>
                {report ? `生成于 ${new Date(report.createdAt).toLocaleString('zh-CN', { hour12: false })}` : '加载中…'}
              </DialogDescription>
            </DialogHeader>
            {query.isError && <EmptyState title="报告加载失败" description={query.error?.message} />}
            {report && (
              <div className="space-y-3 text-xs leading-5">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <Cell label="震级" value={formatMag(report.magnitude)} tone="text-critical" />
                  <Cell label="影响半径" value={`${report.affectedRadiusKm.toFixed(0)} km`} />
                  <Cell label="触发设备" value={String(report.triggeredDevices)} />
                  <Cell label="确认设备" value={String(report.confirmedDevices)} />
                </div>
                {report.summary && (
                  <div className="rounded-lg bg-black/20 p-3">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">摘要</div>
                    <p className="text-foreground/90">{report.summary}</p>
                  </div>
                )}
                {report.content && (
                  <div className="max-h-48 overflow-y-auto rounded-lg bg-black/20 p-3">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">正文</div>
                    <p className="whitespace-pre-wrap text-foreground/80">{report.content}</p>
                  </div>
                )}
                {report.estimatedLoss && (
                  <div className="rounded-lg bg-[var(--warning-soft)] p-3 text-warning">
                    预估损失：{report.estimatedLoss}
                  </div>
                )}
                {attachments.length > 0 && (
                  <div>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">附件</div>
                    <div className="flex flex-wrap gap-1.5">
                      {attachments.map((a) => (
                        <a
                          key={a}
                          href={`/uploads/incidents/${a.replace(/^.*\//, '')}`}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-md border border-border px-2 py-1 font-mono text-[10px] text-primary hover:bg-primary/10"
                        >
                          {a}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Cell({ label, value, tone = 'text-foreground' }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-lg bg-black/20 py-2">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className={`mt-0.5 font-mono text-sm font-semibold tabular-nums ${tone}`}>{value}</div>
    </div>
  )
}
